import { useCallback, useEffect, useState } from "react";
import Lucide from "src/base-components/Lucide";
import { useAppDispatch, useAppSelector } from "src/stores";
import {
  selectColorScheme,
  setColorScheme,
} from "src/stores/basicSlices/colorSchemeSlice";
import {
  applyWebsiteTheme,
  getStoredWebsiteThemeId,
  getWebsiteThemePreset,
} from "src/helpers/theme";
import { buildFourStopGradient } from "src/themes/presets";
import ThemeLibraryDrawer from "./ThemeLibraryDrawer";
import "./theme-customizer.css";

const ThemeCustomizer = () => {
  const dispatch = useAppDispatch();
  const activeThemeId = useAppSelector(selectColorScheme);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const storedThemeId = getStoredWebsiteThemeId();
    applyWebsiteTheme(storedThemeId);
    dispatch(setColorScheme(storedThemeId));
  }, [dispatch]);

  useEffect(() => {
    if (!drawerOpen) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setDrawerOpen(false);
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleEscape);
    };
  }, [drawerOpen]);

  const handleThemeSelect = useCallback(
    (themeId: string) => {
      applyWebsiteTheme(themeId);
      dispatch(setColorScheme(themeId));
    },
    [dispatch],
  );

  const activePreset = getWebsiteThemePreset(activeThemeId);
  const fabGradient = buildFourStopGradient(activePreset.colors, "135deg");

  return (
    <>
      <ThemeLibraryDrawer
        open={drawerOpen}
        activeThemeId={activeThemeId}
        onClose={() => setDrawerOpen(false)}
        onSelectTheme={handleThemeSelect}
      />

      {!drawerOpen ? (
        <button
          type="button"
          aria-label="Open theme library"
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen(true)}
          className="theme-fab group fixed bottom-6 left-1/2 z-[9989] flex -translate-x-1/2 items-center gap-2.5 rounded-full border border-white/25 px-4 py-3 text-white shadow-[0_16px_40px_-12px_rgba(0,0,0,0.55)] transition-all duration-300 hover:-translate-y-0.5 hover:scale-[1.03] hover:shadow-[0_22px_48px_-12px_rgba(0,0,0,0.6)] active:scale-95"
          style={{ background: fabGradient }}
        >
          <span className="absolute inset-0 rounded-full bg-white/0 transition-colors duration-300 group-hover:bg-white/10" />

          <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-white/15 backdrop-blur-sm">
            <Lucide
              icon="Settings"
              className="h-4 w-4 transition-transform duration-300 group-hover:rotate-90"
            />
          </span>

          <span className="relative hidden text-left sm:block">
            <span className="block text-sm font-semibold leading-tight">
              Themes
            </span>
            <span className="block max-w-[9rem] truncate text-[11px] text-white/80">
              {activePreset.name}
            </span>
          </span>

          <span className="relative flex items-center -space-x-1.5 pl-1">
            {activePreset.colors.slice(0, 4).map((color, index) => (
              <span
                key={`${activePreset.id}-dot-${index}`}
                className="h-4 w-4 rounded-full border-2 border-white/80 shadow-sm"
                style={{ backgroundColor: color }}
              />
            ))}
          </span>
        </button>
      ) : null}
    </>
  );
};

export default ThemeCustomizer;
